import { HTMLAttributes } from 'react'
import { default as NextLink } from 'next/link'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Link from '@/components/base/link'
import Media from '@/components/base/media'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type ContactsProps = HTMLAttributes<HTMLElement>

const Contacts = ({ ...props }: ContactsProps) => {
  const { strings } = useStore()
  const { brand, infos, links, labels, google_maps } = strings || {}

  return (
    <Section {...props}>
      <Container>
        <Grid>
          <div
            className={cn(
              'col-span-4 md:col-span-6 lg:col-span-10 lg:col-start-2',
              'grid grid-cols-2 gap-x-gutter gap-y-[3.2rem]'
            )}
          >
            <div className="col-span-2 md:col-span-1">
              <div className="grid gap-y-[3.2rem] md:gap-y-[6.4rem]">
                <div className="grid gap-y-[1.6rem]">
                  {labels?.contact ? (
                    <Typography
                      variant="label"
                      component="h2"
                      className="font-gillsans uppercase text-copper"
                    >
                      {labels?.contact}
                    </Typography>
                  ) : null}

                  {labels?.get_in_touch ? (
                    <Typography
                      variant="h4"
                      component="h1"
                      className="font-timesnow"
                    >
                      {labels?.get_in_touch}
                    </Typography>
                  ) : null}

                  <div className="grid gap-y-[3.2rem]">
                    {infos?.phone || infos?.email ? (
                      <div>
                        {infos?.phone ? (
                          <div className="flex">
                            <a
                              href={links?.phone.url}
                              target={links?.phone.target}
                              className="inline-flex"
                            >
                              <Typography
                                variant="body"
                                className="font-gillsans font-light"
                              >
                                {infos?.phone}
                              </Typography>
                            </a>
                          </div>
                        ) : null}

                        {infos?.email ? (
                          <div className="flex">
                            <a
                              href={links?.email.url}
                              target={links?.email.target}
                              className="inline-flex"
                            >
                              <Typography
                                variant="body"
                                className="font-gillsans font-light"
                              >
                                {infos?.email}
                              </Typography>
                            </a>
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    {infos?.opening_hours ? (
                      <div className="grid gap-y-[1.6rem]">
                        <Typography
                          variant="label"
                          paragraph
                          className="font-gillsans uppercase"
                        >
                          {labels?.opening_hours}
                        </Typography>
                        <Typography
                          variant="body"
                          paragraph
                          className="font-gillsans font-light"
                        >
                          {infos?.opening_hours}
                        </Typography>
                      </div>
                    ) : null}

                    {infos?.piva || infos?.cin ? (
                      <div className="grid gap-y-[1.6rem]">
                        <Typography
                          variant="label"
                          paragraph
                          className="font-gillsans uppercase"
                        >
                          {labels?.other_info}
                        </Typography>
                        <Typography
                          variant="small"
                          paragraph
                          className="font-gillsans font-light"
                        >
                          {`${infos?.piva}<br>${infos?.cin}`}
                        </Typography>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="flex space-x-[3.2rem] md:space-x-[5.2rem]">
                  {links?.whatsapp && links?.whatsapp.url ? (
                    <Link asChild>
                      <NextLink
                        href={links?.whatsapp.url}
                        target={links?.whatsapp.target}
                      >
                        <Typography variant="label">
                          {links?.whatsapp.title}
                        </Typography>
                      </NextLink>
                    </Link>
                  ) : null}

                  {links?.email && links?.email.url ? (
                    <Link asChild>
                      <NextLink
                        href={links?.email.url}
                        target={links?.email.target}
                      >
                        <Typography variant="label">
                          {links?.email.title}
                        </Typography>
                      </NextLink>
                    </Link>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="col-span-2 md:col-span-1">
              <div className="grid gap-y-[3.2rem]">
                <a
                  href={google_maps}
                  target="_blank"
                  className="relative aspect-[500/280] rounded-[0.5rem] border border-havana bg-havana"
                >
                  <Media
                    fill
                    fit="cover"
                    use_video={false}
                    image={{
                      url: '/assets/map.jpg',
                      alt: 'Map'
                    }}
                    className="rounded-[0.5rem]"
                  />
                </a>
                <div className="grid gap-y-[1.6rem]">
                  {brand || infos?.address ? (
                    <div className="grid gap-y-[1.6rem]">
                      {brand ? (
                        <Typography
                          variant="label"
                          paragraph
                          className="font-gillsans uppercase"
                        >
                          {brand}
                        </Typography>
                      ) : null}

                      {infos?.address ? (
                        <Typography
                          variant="small"
                          paragraph
                          className="font-gillsans font-light"
                        >
                          {infos?.address}
                        </Typography>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default Contacts
